import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { Helmet } from 'react-helmet';
import SimpleCard from '../../components/templates/SimpleCard';
import FileCard from '../../components/templates/FileCard';
import Button from '../../components/Button';
import corporateService from '../../axios/services/corporateService';
import styles from './contracts.module.scss';

const ContractView = () => {
  const [contract, setContract] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user, company } = useSelector(state => state.auth);
  const { addToast } = useToasts();

  useEffect(() => {
    loadContract();
  }, []);

  const loadContract = () => {
    if (!company?._id) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    corporateService.getContract(company._id)
      .then((response) => {
        setContract(response.data);
      })
      .catch((error) => {
        if (error.response?.status !== 404) {
          addToast('Gagal memuat kontrak', { appearance: 'error', autoDismiss: true });
        }
      })
      .finally(() => setIsLoading(false));
  };

  const hasTemplate = contract?.contract?.file;
  const hasSignedContract = contract?.contract?.signedFile;
  const contractStatus = contract?.contract?.status;
  const isApproved = contractStatus === 'APPROVED';
  const isRejected = contractStatus === 'REJECTED';
  const isPendingApproval = contractStatus === 'SIGNED_UPLOADED';

  if (isLoading) {
    return (
      <SimpleCard loading={true}>
        <div style={{ height: '200px' }} />
      </SimpleCard>
    );
  }

  return (
    <div>
      <Helmet title="Kontrak Perusahaan" />
      <h1 className="mb-40">Kontrak Perusahaan</h1>
      
      <SimpleCard className={styles.contractCard}>
        {hasTemplate ? (
          <div>
            <div className="row mb-24">
              <div className="col-12">
                <h3 className="mb-16">Kontrak {contract.companyName}</h3>
                <p className="text-muted mb-24">
                  Kode Perusahaan: <strong>{contract.companyCode}</strong>
                </p>
              </div>
            </div>

            {/* Template Contract Section */}
            <div className="row mb-24">
              <div className="col-md-8">
                <h5 className="mb-16">Template Kontrak</h5>
                <FileCard
                  title="Template Kontrak untuk Ditandatangani"
                  filename="kontrak-template.pdf"
                  url={contract.contract.file.url}
                  className="mb-16"
                />
                <div className="alert alert-info">
                  <strong>Instruksi:</strong>
                  <ol className="mb-0 mt-8">
                    <li>Download template kontrak di atas</li>
                    <li>Tanda tangani dokumen secara fisik atau digital</li>
                    <li>Scan/upload dokumen yang sudah ditandatangani</li>
                    <li><strong>Kirim kontrak fisik yang sudah ditandatangani ke:</strong>
                      <div className="mt-12 p-12" style={{ 
                        backgroundColor: '#f8f9fa', 
                        border: '1px solid #dee2e6', 
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}>
                        <div className="font-500 mb-8" style={{ color: '#495057' }}>
                          📍 Kantor PIRO
                        </div>
                        <div className="mb-4">🏢 Millenium Centennial Center</div>
                        <div className="mb-4">🏢 Lantai 38, Suite 3838</div>
                        <div className="mb-4">🛣️ Jl. Jenderal Sudirman Kav. 25</div>
                        <div className="mb-4">📍 Kel. Karet Kuningan, Kec. Setiabudi</div>
                        <div className="mb-0">🌆 Jakarta Selatan, DKI Jakarta</div>
                      </div>
                    </li>
                  </ol>
                  <div className="mt-12 p-8" style={{ 
                    backgroundColor: '#fff3cd', 
                    border: '1px solid #ffeaa7', 
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}>
                    <strong>📋 Catatan Penting:</strong> Pastikan kontrak fisik yang dikirim sama dengan yang diupload secara digital.
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className={styles.contractInfo}>
                  <div className="mb-12">
                    <label className="font-500">Status Template:</label>
                    <span className="badge badge-success ml-8">
                      Tersedia
                    </span>
                  </div>
                  <div className="mb-12">
                    <label className="font-500">Tanggal Upload:</label>
                    <div>{new Date(contract.contract.uploadedAt).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Signed Contract Section */}
            {hasSignedContract && (
              <div className="row mb-24">
                <div className="col-md-8">
                  <h5 className="mb-16">Kontrak Tertandatangan</h5>
                  <FileCard
                    title="Kontrak yang Sudah Ditandatangani"
                    filename="kontrak-signed.pdf"
                    url={contract.contract.signedFile.url}
                    className="mb-16"
                  />
                  {isRejected && contract.contract.rejectionReason && (
                    <div className="alert alert-danger">
                      <h6 className="alert-heading">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        Kontrak Ditolak
                      </h6>
                      <strong>Alasan:</strong> {contract.contract.rejectionReason}
                      <hr />
                      <p className="mb-0">
                        <small>
                          <i className="fas fa-info-circle mr-1"></i>
                          Silakan perbaiki kontrak sesuai catatan di atas, kemudian upload ulang dengan tombol di bawah.
                        </small>
                      </p>
                    </div>
                  )}
                  {isApproved && (
                    <div className="alert alert-success">
                      <h6 className="alert-heading">
                        <i className="fas fa-check-circle mr-2"></i>
                        Kontrak Disetujui!
                      </h6>
                      <p className="mb-0">Kontrak Anda telah disetujui dan aktif. Proses kontrak kerjasama sudah selesai.</p>
                    </div>
                  )}
                  {isPendingApproval && (
                    <div className="alert alert-info">
                      <h6 className="alert-heading">
                        <i className="fas fa-clock mr-2"></i>
                        Sedang Direview
                      </h6>
                      <p className="mb-0">Kontrak tertandatangan sedang direview oleh admin. Harap menunggu konfirmasi.</p>
                    </div>
                  )}
                </div>
                <div className="col-md-4">
                  <div className={styles.contractInfo}>
                    <div className="mb-12">
                      <label className="font-500">Status:</label>
                      <span className={`badge ml-8 ${
                        isApproved ? 'badge-success' : 
                        isRejected ? 'badge-danger' : 
                        'badge-warning'
                      }`}>
                        {isApproved ? 'Disetujui' : 
                         isRejected ? 'Ditolak' : 
                         'Menunggu Persetujuan'}
                      </span>
                    </div>
                    <div className="mb-12">
                      <label className="font-500">Tanggal Upload:</label>
                      <div>{new Date(contract.contract.signedUploadedAt).toLocaleDateString('id-ID', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}</div>
                    </div>
                    {isApproved && contract.contract.approvalDate && (
                      <div className="mb-12">
                        <label className="font-500">Tanggal Disetujui:</label>
                        <div>{new Date(contract.contract.approvalDate).toLocaleDateString('id-ID', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {isPendingApproval && (
              <div className="alert alert-warning text-center">
                <strong>Menunggu Persetujuan</strong>
                <br />
                Kontrak tertandatangan Anda sedang direview oleh admin. Harap tunggu konfirmasi.
              </div>
            )}

            {isApproved && (
              <div className="alert alert-success text-center">
                <strong>Kontrak Telah Disetujui!</strong>
                <br />
                Proses kontrak sudah selesai. Terima kasih atas kerjasamanya.
              </div>
            )}

          </div>
        ) : (
          <div className="text-center" style={{ padding: '60px 20px' }}>
            <div className={styles.noContract}>
              <i className="fas fa-file-contract fa-3x text-muted mb-24"></i>
              <h4 className="mb-16">Belum Ada Template Kontrak</h4>
              <p className="text-muted mb-24">
                Template kontrak belum tersedia.
                <br />
                Admin akan mengupload template kontrak setelah proses verifikasi perusahaan selesai.
              </p>
              <div className="alert alert-info">
                <strong>Langkah Selanjutnya:</strong>
                <ul className="mb-0 mt-8 text-left">
                  <li>Pastikan semua dokumen perusahaan sudah diverifikasi</li>
                  <li>Admin akan mengupload template kontrak</li>
                  <li>Anda akan mendapat notifikasi untuk menandatangani</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </SimpleCard>

    </div>
  );
};

export default ContractView;
